/**
 * WaveHeroV2 — the always-expanded Hero accordion body (#1 · Campaign
 * Performance). Locked spec, Feb 2026 redesign:
 *
 *   ┌─ HERO BODY ────────────────────────────────────────────────────┐
 *   │   ╭───╮      Engagement ─────────────● 82                       │
 *   │   │ 78│      Reach      ──────────● 74                          │
 *   │   │ +5│      Velocity   ─────────────────● 91                   │
 *   │   ╰───╯      Approval   ────────● 68                            │
 *   │              Diversity  ──────────● 75   🌊 LOCKED              │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │   Pulse is trending UP because Velocity is compounding...      │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │   REACH 329K · CPM $3.65 · ENGAGEMENTS 22K · ROAS 4.2×         │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │   ▲ BOOST BUDGET NOW · projected +8 Pulse points  [Boost Now →]│
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Data sources
 * ────────────
 *   • Pulse score + trend     → useWavePulse(waveId)
 *   • 5 composite scores      → pulseData.composites[{eng,reach,vel,app,div}]
 *   • Top-driver narrative    → cmdData.composite_insights[topCompositeName]
 *   • 4 KPI tiles             → totals + kpiBenchmarks/kpiProgress (existing)
 *   • Live ROAS override      → useLensROAS (Lens A · Wave 1 fusion)
 *
 * Design rules
 * ────────────
 *   • Diversity bar muted purple + 🌊 lock icon — user-locked decision
 *   • Boost Budget CTA only renders when Pulse ≥ 75 AND trend ≥ +3
 *   • Top-driver narrative ONLY surfaces the strongest non-diversity
 *     composite, since diversity doesn't move during a wave.
 */
import React from 'react';
import { Loader2, Waves, TrendingUp, TrendingDown, Zap } from 'lucide-react';
import useWavePulse from '../../hooks/useWavePulse';
import useLensROAS from '../../hooks/useLensROAS';

const COMPOSITE_ORDER = ['engagement', 'reach', 'velocity', 'approval', 'diversity'];
const COMPOSITE_LABEL = {
  engagement: 'Engagement',
  reach:      'Reach',
  velocity:   'Velocity',
  approval:   'Approval',
  diversity:  'Diversity',
};

// ─── Color helpers ──────────────────────────────────────────────────────
function pulseColor(score) {
  if (score >= 75) return '#4ade80';   // green
  if (score >= 45) return '#00D4FF';   // teal
  return '#fbbf24';                    // amber
}
function compositeColor(score) {
  if (score >= 75) return '#4ade80';
  if (score >= 60) return '#00D4FF';
  if (score >= 40) return '#fbbf24';
  return '#FF6B6B';
}
function formatCompact(n) {
  if (n == null || isNaN(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return `${Math.round(n)}`;
}

// ─── Pulse ring (re-implementation matching mock — single big number) ──
function PulseRing({ score, trendDelta, ghostTarget = null }) {
  const color = pulseColor(score);
  const R = 100;
  const C = 2 * Math.PI * R;
  const dashOn = (Math.max(0, Math.min(100, score)) / 100) * C;
  const trendUp = (trendDelta || 0) > 0;
  const trendDown = (trendDelta || 0) < 0;
  // Ghost target — the projected_pulse_target carried over from the briefing
  // (iter-89 continuity). Renders as a thin pink tick on the ring at the
  // target % position so the brand SEES the goalpost without us fabricating
  // current composite numbers. Honest projection, not speculative current.
  const hasGhost = Number.isFinite(ghostTarget) && ghostTarget > 0 && ghostTarget <= 100;
  const ghostPct = hasGhost ? Math.max(0, Math.min(100, ghostTarget)) : 0;
  // Tick is a tiny arc (~6° wide) at the ghost angle on the ring.
  const ghostArcLen = (6 / 360) * C;
  const ghostOffset = (ghostPct / 100) * C - ghostArcLen / 2;
  return (
    <div className="relative w-[220px] h-[220px] flex items-center justify-center" data-testid="hero-pulse-ring">
      <svg viewBox="0 0 220 220" className="absolute inset-0">
        <circle cx="110" cy="110" r={R} stroke="#1a1f2b" strokeWidth="14" fill="none" />
        {hasGhost && (
          <circle
            data-testid="hero-pulse-ghost-target"
            cx="110" cy="110" r={R}
            stroke="#FF4D94"
            strokeWidth="14"
            fill="none"
            strokeLinecap="butt"
            strokeDasharray={`${ghostArcLen} ${C - ghostArcLen}`}
            strokeDashoffset={-ghostOffset}
            transform="rotate(-90 110 110)"
            opacity={0.65}
            style={{ filter: 'drop-shadow(0 0 6px rgba(255,77,148,0.8))' }}
          />
        )}
        <circle
          cx="110" cy="110" r={R}
          stroke={color}
          strokeWidth="14"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dashOn} ${C - dashOn}`}
          transform="rotate(-90 110 110)"
          style={{ filter: `drop-shadow(0 0 12px ${color}59)` }}
        />
      </svg>
      <div className="flex flex-col items-center">
        <span
          className="text-[88px] font-black leading-none tracking-[-0.04em] tabular-nums"
          style={{ color }}
          data-testid="hero-pulse-score"
        >
          {score}
        </span>
        <span className="text-[9.5px] uppercase tracking-[0.24em] font-black text-gray-500 mt-1">
          Wave Pulse
        </span>
        {hasGhost && score === 0 && (
          <span
            data-testid="hero-pulse-target"
            className="text-[10px] font-black mt-1.5 uppercase tracking-[0.18em]"
            style={{ color: '#FF4D94' }}
          >
            target {ghostPct}
          </span>
        )}
        {(trendUp || trendDown) && (
          <span
            className="text-[11px] font-black mt-1 inline-flex items-center gap-1"
            style={{ color: trendUp ? '#4ade80' : '#FF6B6B' }}
            data-testid="hero-pulse-trend"
          >
            {trendUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {trendUp ? '+' : ''}{trendDelta} (60-min)
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Composite bar — single 0-100 horizontal gauge ──────────────────────
function CompositeBar({ name, score, locked = false, warmingUp = false }) {
  const color = locked ? '#A78BFA' : compositeColor(score);
  const pct = Math.max(0, Math.min(100, score || 0));
  // When the wave just fired and the engine hasn't published its first
  // composite tick yet, the bar is honestly 0 — but we render a subtle
  // teal "warming up" indicator on the track so the brand doesn't read
  // the bar as broken. The bar fill stays at 0% — no fabrication.
  const isWarming = warmingUp && pct === 0 && !locked;
  return (
    <div
      data-testid={`hero-composite-${name}`}
      data-warming={isWarming ? 'true' : 'false'}
      className="grid grid-cols-[100px_1fr_36px] items-center gap-3"
    >
      <div className="flex items-center gap-1.5">
        <span
          className="text-[12px] font-black uppercase tracking-[0.08em]"
          style={{ color: locked ? '#cbd5e1' : '#fff' }}
        >
          {COMPOSITE_LABEL[name]}
        </span>
        {locked && (
          <span
            data-testid={`hero-composite-${name}-lock`}
            title="Set at wave launch — adjust on next wave's roster"
            className="inline-flex"
          >
            <Waves className="h-3 w-3 flex-shrink-0" style={{ color: '#A78BFA' }} />
          </span>
        )}
      </div>
      <div
        className="h-2 rounded-full overflow-hidden relative"
        style={{ background: '#0F0F0F', border: '1px solid #1a1f2b' }}
      >
        {isWarming && (
          <div
            data-testid={`hero-composite-${name}-warming`}
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(0,212,255,0.30) 50%, transparent 100%)',
              backgroundSize: '60% 100%',
              animation: 'compositeWarmUp 1800ms ease-in-out infinite',
            }}
          />
        )}
        <div
          className="h-full rounded-full transition-[width] duration-700"
          style={{
            width: `${pct}%`,
            background: color,
            opacity: locked ? 0.55 : 1,
          }}
        />
      </div>
      <span
        className="text-[13.5px] font-black tabular-nums tracking-[-0.02em] text-right"
        style={{
          color: isWarming ? '#475569' : color,
          opacity: locked ? 0.7 : 1,
        }}
      >
        {isWarming ? '—' : Math.round(pct)}
      </span>
    </div>
  );
}

// ─── KPI strip (4 universal tiles) ──────────────────────────────────────
function KpiStrip({ tiles }) {
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-xl overflow-hidden mt-4"
      style={{ background: '#2E3240', border: '1px solid #2E3240' }}
      data-testid="hero-kpi-strip"
    >
      {tiles.map(t => (
        <div
          key={t.label}
          className="px-4 py-3"
          style={{ background: '#181A20' }}
          data-testid={t.testId}
        >
          <p className="text-[9.5px] text-gray-500 uppercase tracking-[0.18em] font-black">
            {t.label}
            {t.live && (
              <span
                className="ml-1.5 text-[8px] font-black px-1 rounded"
                style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}
              >
                LIVE
              </span>
            )}
          </p>
          <p
            className="text-[22px] font-black mt-1 tracking-[-0.02em] tabular-nums"
            style={{ color: t.color }}
          >
            {t.value}
          </p>
          {t.sub && (
            <p className="text-[10px] text-gray-500 mt-0.5">{t.sub}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Variable-KPI picker (lifted from old WavePulseHero) ───────────────
//
// Safe-surface contract (iter-89): ROAS is a speculative metric until the
// SSP integration has empirical wave outcome data. The picker skips 'roas'
// by default. ROAS is re-injected by the caller IF AND ONLY IF
// `useLensROAS(waveId).hasData === true` — that hook returns true only
// when an SSP-fused outcome row exists for this wave. Same contract that
// governs the briefing payload governs every surface the brand sees after
// ignition.
function pickVariableKpi(benchmarks = [], progress = []) {
  for (const b of benchmarks || []) {
    if (!b || !b.key) continue;
    if (b.key === 'reach') continue;
    if (b.key === 'roas') continue;  // Gated separately — see ROAS calibration block above.
    const p = (progress || []).find(x => x.key === b.key);
    if (!p) continue;
    return { ...b, current: p.current };
  }
  return null;
}
function formatVariableValue(v) {
  if (!v) return '—';
  const u = (v.unit || '').toString();
  if (u === '×' || u === 'x') return `${(v.current || 0).toFixed(2)}×`;
  if (u === '%')              return `${(v.current || 0).toFixed(1)}%`;
  if (u === '$' || v.key === 'gmv' || v.key === 'revenue')
    return `$${formatCompact(v.current)}`;
  if (typeof v.current === 'number' && v.current >= 1000)
    return formatCompact(v.current);
  return `${v.current}`;
}
function formatVariableTarget(v) {
  if (!v?.target) return null;
  const u = (v.unit || '').toString();
  if (u === '×' || u === 'x') return `vs ${v.target.toFixed(1)}×`;
  if (u === '%')              return `vs ${v.target.toFixed(1)}%`;
  if (u === '$' || v.key === 'gmv' || v.key === 'revenue')
    return `vs $${formatCompact(v.target)}`;
  return `vs ${formatCompact(v.target)}`;
}

// ─── Top driver narrative ───────────────────────────────────────────────
function topComposite(composites) {
  // Highest-scoring non-diversity composite (diversity doesn't move).
  const candidates = COMPOSITE_ORDER
    .filter(k => k !== 'diversity')
    .map(k => ({ key: k, ...(composites?.[k] || {}) }))
    .filter(c => typeof c.score === 'number');
  if (!candidates.length) return null;
  candidates.sort((a, b) => (b.score || 0) - (a.score || 0));
  return candidates[0];
}
function trendVerb(delta) {
  if (delta > 0)  return { word: 'UP', color: '#4ade80' };
  if (delta < 0)  return { word: 'DOWN', color: '#FF6B6B' };
  return { word: 'FLAT', color: '#cbd5e1' };
}

// ─── Boost Budget CTA ───────────────────────────────────────────────────
function BoostBudgetCTA({ pulse, trend, onBoostBudget }) {
  if (pulse < 75 || trend < 3) return null;
  const projected = Math.round(Math.min(15, Math.max(4, trend * 1.6)));
  return (
    <button
      type="button"
      onClick={onBoostBudget}
      data-testid="hero-boost-budget-cta"
      className="mt-4 w-full flex items-center justify-between gap-4 px-5 py-4 rounded-xl text-left transition-transform hover:scale-[1.005]"
      style={{
        background: 'linear-gradient(135deg, rgba(74,222,128,0.10), rgba(0,212,255,0.06))',
        border: '1px solid rgba(74,222,128,0.30)',
      }}
    >
      <div>
        <p className="text-[10.5px] font-black uppercase tracking-[0.16em]" style={{ color: '#4ade80' }}>
          ▲ Pulse climbing · budget injection recommended
        </p>
        <p className="text-[14.5px] font-bold text-white mt-1 leading-snug">
          Extend the peak — projected <span style={{ color: '#4ade80' }}>+{projected} Pulse points</span>
        </p>
        <p className="text-[11px] text-gray-400 mt-1">
          Fires Spawn Burst on top driving team · ~$1.2K/creator · 3–6 new creators
        </p>
      </div>
      <span
        className="px-5 py-2.5 rounded-lg text-[12px] font-black uppercase tracking-[0.06em] flex-shrink-0"
        style={{ background: '#4ade80', color: '#062314' }}
      >
        Boost Now →
      </span>
    </button>
  );
}

// ─── Main hero ──────────────────────────────────────────────────────────
export default function WaveHeroV2({
  waveId,
  pulseData,         // { pulse_score, composites: {...} } from useWavePulseDetail
  totals = {},
  kpiBenchmarks = [],
  kpiProgress = [],
  cmdInsights = {},
  onBoostBudget = () => {},
  // iter-89 continuity from briefing → control room
  ghostTarget = null,          // projected_pulse_target carried over via location.state
  warmingUp = false,           // true when the wave engine hasn't published composites yet
}) {
  // Live pulse hook drives the ring score + delta (more authoritative than
  // pulseData.pulse_score; falls back to it when SSE not connected).
  const { pulse, delta, isLoading } = useWavePulse(waveId);
  const liveROAS = useLensROAS(waveId);

  const score = (pulse ?? pulseData?.pulse_score ?? 0);
  const composites = pulseData?.composites || {};

  // Variable KPI (4th tile) — picker now excludes 'roas' by default.
  // ROAS is a CALIBRATION-GATED metric: re-inject it ONLY when the SSP
  // integration has confirmed empirical outcome data (useLensROAS
  // returns `hasData=true`). Without empirical data, ROAS is speculative
  // and would violate the safe-surface contract that governs every
  // surface the brand sees after ignition. When ROAS isn't available
  // and no other variable KPI fits, the 4th tile is simply omitted.
  let variableKpi = pickVariableKpi(kpiBenchmarks, kpiProgress);
  if (!variableKpi && liveROAS.hasData) {
    const roasBench = (kpiBenchmarks || []).find(k => k.key === 'roas') || {
      key: 'roas', label: 'ROAS', unit: '×',
    };
    variableKpi = {
      ...roasBench,
      current: liveROAS.roas,
      liveSource: 'ssp',
    };
  }
  const reachBench = (kpiBenchmarks || []).find(k => k.key === 'reach');

  const tiles = [
    {
      label: 'Reach',
      value: formatCompact(totals.total_reach || totals.total_views),
      sub:   reachBench?.target ? `vs ${formatCompact(reachBench.target)}` : null,
      color: '#00D4FF',
      testId: 'hero-kpi-reach',
    },
    {
      label: 'CPM',
      value: totals.cpm_usd ? `$${totals.cpm_usd.toFixed(2)}` : '—',
      sub:   'paid-equiv',
      color: '#A78BFA',
      testId: 'hero-kpi-cpm',
    },
    {
      label: 'Engagements',
      value: formatCompact(totals.total_engagement),
      sub:   null,
      color: '#FF4D94',
      testId: 'hero-kpi-engagements',
    },
    variableKpi
      ? {
          label: variableKpi.label,
          value: formatVariableValue(variableKpi),
          sub:   formatVariableTarget(variableKpi),
          color: '#4ade80',
          testId: `hero-kpi-${variableKpi.key}`,
          live:  !!variableKpi.liveSource,
        }
      : null,
  ].filter(Boolean);

  // Internal loading guard — was the "Computing Wave Pulse…" spinner
  // that fired on first paint. The Control Room (iter-89) now renders
  // a skeleton-first chrome with seeded values, so this internal
  // spinner makes the Hero feel "less ready" than its surroundings.
  // We only render the spinner when there's NO continuity (not from
  // ignition) AND no score AND we're actively loading.
  if (!warmingUp && isLoading && score === 0) {
    return (
      <div className="flex items-center gap-3 py-8" data-testid="hero-loading">
        <Loader2 className="h-5 w-5 animate-spin text-[#00D4FF]" />
        <span className="text-sm text-gray-400">Computing Wave Pulse…</span>
      </div>
    );
  }

  const top = topComposite(composites);
  const verb = trendVerb(delta || 0);
  const insight = top ? (cmdInsights?.[top.key]?.analyst_sentence || top.translation) : null;

  return (
    <div data-testid="wave-hero-v2">
      {/* Pulse ring + 5 composite bars */}
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8 items-center">
        <div className="flex justify-center md:justify-start">
          <PulseRing
            score={Math.round(score)}
            trendDelta={Math.round(delta || 0)}
            ghostTarget={ghostTarget}
          />
        </div>
        <div className="flex flex-col gap-3.5">
          {COMPOSITE_ORDER.map(name => {
            const c = composites[name] || {};
            const locked = name === 'diversity';
            return (
              <CompositeBar
                key={name}
                name={name}
                score={c.score ?? 0}
                locked={locked}
                warmingUp={warmingUp}
              />
            );
          })}
        </div>
      </div>

      {/* Warm-up keyframe — subtle teal sweep across composite bars when
          the wave just fired and the engine hasn't published composites
          yet. Bars stay at 0%, no fabrication. */}
      <style>{`
        @keyframes compositeWarmUp {
          0%   { background-position: -150% 0; }
          100% { background-position:  150% 0; }
        }
      `}</style>

      {/* Trending narrative band */}
      {top && (
        <div
          className="mt-5 rounded-lg px-4 py-3 text-[13px] leading-relaxed"
          style={{
            background: '#1a1f2b',
            borderLeft: `3px solid ${verb.color}`,
            color: '#e2e8f0',
          }}
          data-testid="hero-trending-narrative"
        >
          Pulse is trending <b style={{ color: verb.color }}>{verb.word}</b>
          {' '}because <b style={{ color: compositeColor(top.score) }}>{COMPOSITE_LABEL[top.key]}</b>
          {' '}is the strongest composite right now.
          {insight && (
            <span className="text-gray-400"> {insight}</span>
          )}
        </div>
      )}

      {/* KPI strip */}
      <KpiStrip tiles={tiles} />

      {/* Boost Budget CTA — only when Pulse ≥ 75 AND trend ≥ +3 */}
      <BoostBudgetCTA
        pulse={Math.round(score)}
        trend={Math.round(delta || 0)}
        onBoostBudget={onBoostBudget}
      />
    </div>
  );
}
